// src/rms/hooks/useRmsAnalytics.js
/**
 * useRmsAnalytics
 * ---------------
 * What:
 *   Thin wrapper around Firebase Analytics to emit typed RMS events.
 *
 * Events (payload suggestions):
 *   - referral_intent_started: { prospectId?, formKey, resumed?: boolean }
 *   - step_viewed:             { stepId }
 *   - step_saved:              { stepId, prospectId? }
 *   - referral_intent_submitted:{ prospectId?, formKey }
 *   - eligibility_checked:     { formKey, outwardCode? }
 *   - eligibility_passed:      { prospectId?, reason?: null }
 *   - eligibility_failed:      { prospectId?, reason?: 'OUT_OF_AREA'|'INVALID_POSTCODE'|... }
 *   - sign_in_invite_superseded: {}
 *   - error_generic:           { where: string, code?: string }
 *
 * Notes:
 *   - Keep payloads small and non‑PII.
 *   - Free-text fields (message/error_message) are stripped before emission:
 *     error strings can embed emails or other PII, so only enum-ish codes leave
 *     the app. Pass `code`, not `message`.
 *   - This wrapper swallows analytics errors; analytics is non‑critical telemetry.
 */
import { useCallback, useMemo } from 'react';
import { analytics } from '../../firebase';
import { logEvent } from 'firebase/analytics';
import { bridgeGa4Event } from '../../analytics/ga4Bridge';

export default function useRmsAnalytics(orgId) {
  const safeLog = useCallback((name, params = {}) => {
    // `|| {}` matters: this destructure used to sit inside the try below, so a
    // caller passing null was swallowed. Keeping it safe preserves that
    // behaviour exactly rather than newly throwing into an RMS flow.
    const { message, error_message, errorMessage, ...safeParams } = params || {};
    try {
      if (analytics) {
        logEvent(analytics, name, safeParams);
      }
    } catch (_) {
      // swallow analytics errors
    }
    // Dual-write into the first-party BigQuery stream from the same call, so
    // GA4 and BigQuery cannot drift apart (src/analytics/ga4Bridge.js). Fed the
    // already-stripped params: free text never reaches either destination.
    bridgeGa4Event(name, safeParams);
  }, []);

  return useMemo(() => ({
    intentStarted: (payload = {}) => safeLog('referral_intent_started', { orgId, ...payload }),
    stepViewed: (payload = {}) => safeLog('step_viewed', { orgId, ...payload }),
    stepSaved: (payload = {}) => safeLog('step_saved', { orgId, ...payload }),
    intentSubmitted: (payload = {}) => safeLog('referral_intent_submitted', { orgId, ...payload }),
    // Post-account forms (under referrals)
    postFormStarted: (payload = {}) => safeLog('post_form_started', { orgId, ...payload }),
    postFormSubmitted: (payload = {}) => safeLog('post_form_submitted', { orgId, ...payload }),
    eligibilityChecked: (payload = {}) => safeLog('eligibility_checked', { orgId, ...payload }),
    eligibilityPassed: (payload = {}) => safeLog('eligibility_passed', { orgId, ...payload }),
    eligibilityFailed: (payload = {}) => safeLog('eligibility_failed', { orgId, ...payload }),
    inviteSuperseded: (payload = {}) => safeLog('sign_in_invite_superseded', { orgId, ...payload }),
    error: (payload = {}) => safeLog('error_generic', { orgId, ...payload }),
  }), [orgId, safeLog]);
}


